import { z } from 'zod'

import {
  newsScopeSchema,
  newsSectorSchema,
  newsShowSchema,
  newsSortSchema,
  newsSourceSchema,
} from '@/schemas/news'

/**
 * Market › News: stories from the news sites (Artha Sarokar, Artha Sansar, Artha Kendra…), the
 * mock's second tab beside Updates, its article page and its source-site reader.
 *
 * mm-be has no news endpoint (`/market/updates/` is the exchange's own bullet posts), so this is
 * the shape the client asks for, written down in `mm-market-2026-10-28/asks/nw1.md`. Every read
 * of it sits behind `marketNewsService.available`.
 */

/** The Filters dialog's Language switch; `ne` stories are Devanagari throughout. */
export const newsLangSchema = z.enum(['en', 'ne'])

/** One card in the News list. */
export const newsArticleSummarySchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  summary: z.string().min(1),
  /** The news site, already worded: "Artha Sarokar". */
  source: z.string().min(1),
  /** "1 Ashwin 2083, 3:10 PM" in Kathmandu time. */
  publishedLabel: z.string().min(1),
  /** The story's own photo, cropped to the card's 136×104; absent draws no thumb at all. */
  thumbnailUrl: z.url().optional(),
  symbol: z.string().min(1).optional(),
})

export const newsArticlePageSchema = z.object({
  rows: z.array(newsArticleSummarySchema),
  nextCursor: z.string().optional(),
  total: z.number().int().nonnegative(),
})

/** Mitra's one-paragraph explainer under a story: "What a book closure means". */
export const newsNoteSchema = z.object({
  title: z.string().min(1),
  body: z.string().min(1),
})

/** One story in full: the preview dialog reads `summary`, the article page reads `body`. */
export const newsArticleSchema = z.object({
  id: z.string().min(1),
  lang: newsLangSchema,
  title: z.string().min(1),
  /** The site that carried it, over the headline. */
  source: z.string().min(1),
  /** Who wrote it, the line under the headline. */
  byline: z.string().min(1),
  /** "1 Ashwin 2083, 2:15 PM": the preview's date chip. */
  publishedLabel: z.string().min(1),
  /** "Published 1 Ashwin 2083, 2:15 PM (17 Sept 2026)": the article's byline line. */
  publishedLine: z.string().min(1),
  heroImageUrl: z.url().optional(),
  /** The preview's short paragraphs. */
  summary: z.array(z.string().min(1)).min(1),
  /** The article's paragraphs. */
  body: z.array(z.string().min(1)).min(1),
  symbol: z.string().min(1).optional(),
  note: newsNoteSchema.optional(),
  /** The story on the news site itself. */
  sourceUrl: z.url(),
  /** "arthasarokar.com", under the reader's headline. */
  sourceDomain: z.string().min(1),
  /**
   * A page mm-be serves that may be framed (the source sites forbid it). Absent, "Read on the
   * source website" opens `sourceUrl` in a new tab instead of the reader panel.
   */
  readerUrl: z.url().optional(),
})

export const newsArticlesQuerySchema = z.object({
  scope: newsScopeSchema.optional(),
  query: z.string().optional(),
  sort: newsSortSchema,
  show: newsShowSchema.optional(),
  sectors: z.array(newsSectorSchema).optional(),
  sources: z.array(newsSourceSchema).optional(),
  lang: newsLangSchema,
})

export type NewsLang = z.infer<typeof newsLangSchema>
export type NewsArticleSummary = z.infer<typeof newsArticleSummarySchema>
export type NewsArticlePage = z.infer<typeof newsArticlePageSchema>
export type NewsNote = z.infer<typeof newsNoteSchema>
export type NewsArticle = z.infer<typeof newsArticleSchema>
export type NewsArticlesQuery = z.infer<typeof newsArticlesQuerySchema>
